<script setup lang="ts">
/**
 * Vidéo d'un module de formation, glissée dans un article de blog
 * (utils/blogVideosFormation.ts). Le blog est en français : textes du module
 * en français, libellés du bloc en dur comme le reste de la page article.
 *
 * Rien ne se télécharge avant le clic (`preload="none"`) : seule l'affiche
 * s'affiche, la page garde sa vitesse. Le titre et le résumé du module restent
 * en texte (les IA lisent le texte, pas la vidéo) ; aucun `<h2>`/`<h3>` pour ne
 * pas toucher à la structure de titres de l'article.
 */
import { trouverFormation } from "~/data/formations/catalogue";
import { chargerTextesFormation, urlAffiche, urlVideo, videoPubliee } from "~/data/formations/textes";
import type { VideoArticle } from "~/utils/blogVideosFormation";

const props = defineProps<{ video: VideoArticle }>();

const formation = trouverFormation(props.video.formation);
const moduleFormation = formation?.modules.find((m) => m.id === props.video.module);
const publiee = videoPubliee(moduleFormation?.tutoriel);

const { data: textes } = await useAsyncData(
  `blog-video-${props.video.formation}-${props.video.module}`,
  () => chargerTextesFormation(props.video.formation, "fr"),
);
const textesModule = computed(() => textes.value?.modules[props.video.module] ?? null);
const titre = computed(() => props.video.titre ?? textesModule.value?.titre ?? "");
const resume = computed(() => props.video.resume ?? textesModule.value?.resume ?? "");

const duree = computed(() => {
  const s = publiee?.duree ?? 0;
  if (!s) return null;
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return min ? `${min} min ${String(sec).padStart(2, "0")}` : `${sec} s`;
});
</script>

<template>
  <aside
    v-if="formation && moduleFormation?.tutoriel && publiee && textesModule"
    class="my-10 overflow-hidden rounded-[24px] border border-gray-200 bg-[#f5f5f7] dark:border-gray-800 dark:bg-gray-900"
    :aria-label="`En vidéo : ${titre}`"
  >
    <video
      class="block aspect-video w-full bg-black"
      controls
      playsinline
      preload="none"
      :poster="urlAffiche(moduleFormation.tutoriel)"
    >
      <source :src="urlVideo(moduleFormation.tutoriel)" type="video/mp4">
    </video>
    <div class="flex flex-col gap-2 p-6 sm:p-7">
      <p class="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#6e6e73]">
        En vidéo<span v-if="duree"> · {{ duree }}</span>
      </p>
      <p class="text-[20px] font-bold leading-snug text-gray-900 dark:text-white">{{ titre }}</p>
      <p class="text-[16px] leading-relaxed text-gray-600 dark:text-gray-300">{{ resume }}</p>
      <NuxtLink
        :to="`/formations/${formation.slug}#module-${moduleFormation.id}`"
        class="mt-1 inline-flex w-fit items-center gap-1 text-[15px] font-semibold text-(--color-main) hover:underline"
      >
        Voir la formation complète, gratuite
        <UIcon name="i-heroicons-arrow-right" class="size-4" />
      </NuxtLink>
    </div>
  </aside>
</template>
